import { flip, offset, shift, size } from '@floating-ui/react';
import { PluginKey } from '@tiptap/pm/state';
import { useCallback, useEffect, useRef, useState } from 'react';

// --- Hooks ---
import { useFloatingElement } from '@/hooks/use-floating-element';
import { useMenuNavigation } from '@/hooks/use-menu-navigation';
import { useTiptapEditor } from '@/hooks/use-tiptap-editor';

// --- Tiptap Editor ---
import type { Range } from '@tiptap/react';

// --- Tiptap UI ---
import { Suggestion } from '@tiptap/suggestion';

// --- UI Primitives ---
import { SuggestionPluginKey, type SuggestionKeyDownProps, type SuggestionProps } from '@tiptap/suggestion';

import type { SuggestionItem, SuggestionMenuProps } from '@/components/tiptap-editor/tiptap-ui-utils/suggestion-menu/suggestion-menu-types';
import { calculateStartPosition } from '@/components/tiptap-editor/tiptap-ui-utils/suggestion-menu/suggestion-menu-utils';

const suggestionMenuOpenCountByEditor = new WeakMap<NonNullable<ReturnType<typeof useTiptapEditor>['editor']>, number>();

// This is needed so that the drag handles are locked when the suggestion menu is open. Otherwise the
// suggestion menu does not render correctly.

function lockDragHandles(editor: NonNullable<ReturnType<typeof useTiptapEditor>['editor']>, locked: boolean) {
    if (editor.isDestroyed) {
        return;
    }
    const { view } = editor;
    view.dispatch(view.state.tr.setMeta('lockDragHandle', locked));
}

function incrementSuggestionMenuOpenCount(editor: NonNullable<ReturnType<typeof useTiptapEditor>['editor']>) {
    const current = suggestionMenuOpenCountByEditor.get(editor) ?? 0;
    if (current === 0) {
        lockDragHandles(editor, true);
    }
    suggestionMenuOpenCountByEditor.set(editor, current + 1);
}

function decrementSuggestionMenuOpenCount(editor: NonNullable<ReturnType<typeof useTiptapEditor>['editor']>) {
    const current = suggestionMenuOpenCountByEditor.get(editor) ?? 0;
    const next = Math.max(0, current - 1);

    if (next === 0) {
        lockDragHandles(editor, false);
        suggestionMenuOpenCountByEditor.delete(editor);
        return;
    }

    suggestionMenuOpenCountByEditor.set(editor, next);
}

/**
 * A component that renders a suggestion menu for Tiptap editors.
 * Displays a floating menu when a trigger character is typed.
 */
export const SuggestionMenu = ({
    editor: providedEditor,
    floatingOptions,
    selector = 'tiptap-suggestion-menu',
    children,
    maxHeight = 384,
    pluginKey = SuggestionPluginKey,
    ...internalSuggestionProps
}: SuggestionMenuProps) => {
    const { editor } = useTiptapEditor(providedEditor);

    const [show, setShow] = useState<boolean>(false);
    const didIncrementOpenCountRef = useRef(false);

    // If later we want the floating stick to the position while browser is scrolling,
    // we can uncomment this part and pass the getBoundingClientRect prop to FloatingElement instead of referenceElement.
    // const [internalClientRect, setInternalClientRect] = useState<DOMRect | null>(
    //   null
    // )
    const [internalDecorationNode, setInternalDecorationNode] = useState<HTMLElement | null>(null);
    const [internalCommand, setInternalCommand] = useState<((item: SuggestionItem) => void) | null>(null);
    const [internalItems, setInternalItems] = useState<SuggestionItem[]>([]);
    const [internalQuery, setInternalQuery] = useState<string>('');
    const [, setInternalRange] = useState<Range | null>(null);

    const { ref, style, getFloatingProps, isMounted } = useFloatingElement(show, internalDecorationNode, 1000, {
        placement: 'bottom-start',
        middleware: [
            offset(10),
            flip({
                mainAxis: true,
                crossAxis: false,
            }),
            shift(),
            size({
                apply({ availableHeight, elements }) {
                    if (elements.floating) {
                        const maxHeightValue = maxHeight ? Math.min(maxHeight, availableHeight) : availableHeight;

                        elements.floating.style.setProperty('--suggestion-menu-max-height', `${maxHeightValue}px`);
                    }
                },
            }),
        ],
        onOpenChange(open) {
            if (!open) {
                setShow(false);
            }
        },
        ...floatingOptions,
    });

    const internalSuggestionPropsRef = useRef(internalSuggestionProps);

    useEffect(() => {
        internalSuggestionPropsRef.current = internalSuggestionProps;
    }, [internalSuggestionProps]);

    const closePopup = useCallback(() => {
        setShow(false);
    }, []);

    useEffect(() => {
        if (!editor || editor.isDestroyed) {
            return;
        }

        // If we unmount while a menu is open, ensure drag handles are unlocked.
        return () => {
            if (didIncrementOpenCountRef.current) {
                decrementSuggestionMenuOpenCount(editor);
                didIncrementOpenCountRef.current = false;
            }
        };
    }, [editor]);

    useEffect(() => {
        if (!editor || editor.isDestroyed) {
            return;
        }

        const existingPlugin = editor.state.plugins.find(plugin => plugin.spec.key === pluginKey);
        if (existingPlugin) {
            editor.unregisterPlugin(pluginKey);
        }

        const suggestion = Suggestion({
            pluginKey: pluginKey instanceof PluginKey ? pluginKey : new PluginKey(pluginKey),
            editor,

            allow(props) {
                const $from = editor.state.doc.resolve(props.range.from);

                // Check if we're inside an image node
                for (let depth = $from.depth; depth > 0; depth--) {
                    if ($from.node(depth).type.name === 'image') {
                        return false; // Don't allow slash command inside image (since we support captions)
                    }
                }

                return true;
            },

            command({ editor, range, props }) {
                if (!range) {
                    return;
                }

                const { view, state } = editor;
                const { selection } = state;

                const isMention = editor.extensionManager.extensions.some(extension => {
                    const name = extension.name;
                    return name === 'mention' && extension.options?.suggestion?.char === internalSuggestionPropsRef.current.char;
                });

                if (!isMention) {
                    const cursorPosition = selection.$from.pos;
                    const previousNode = selection.$head?.nodeBefore;

                    const startPosition = previousNode
                        ? calculateStartPosition(cursorPosition, previousNode, internalSuggestionPropsRef.current.char)
                        : selection.$from.start();

                    const transaction = state.tr.deleteRange(startPosition, cursorPosition);
                    view.dispatch(transaction);
                }

                const nodeAfter = view.state.selection.$to.nodeAfter;
                const overrideSpace = nodeAfter?.text?.startsWith(' ');

                const rangeToUse = { ...range };

                if (overrideSpace) {
                    rangeToUse.to += 1;
                }

                props.onSelect({ editor, range: rangeToUse, context: props.context });
            },

            render: () => {
                return {
                    onStart: (props: SuggestionProps<SuggestionItem>) => {
                        if (!didIncrementOpenCountRef.current) {
                            incrementSuggestionMenuOpenCount(editor);
                            didIncrementOpenCountRef.current = true;
                        }
                        setInternalDecorationNode((props.decorationNode as HTMLElement) ?? null);
                        setInternalCommand(() => props.command);
                        setInternalItems(props.items);
                        setInternalQuery(props.query);
                        setInternalRange(props.range);
                        // setInternalClientRect(props.clientRect?.() ?? null)
                        setShow(true);
                    },

                    onUpdate: (props: SuggestionProps<SuggestionItem>) => {
                        setInternalDecorationNode((props.decorationNode as HTMLElement) ?? null);
                        setInternalCommand(() => props.command);
                        setInternalItems(props.items);
                        setInternalQuery(props.query);
                        setInternalRange(props.range);
                        // setInternalClientRect(props.clientRect?.() ?? null)
                    },

                    onKeyDown: (props: SuggestionKeyDownProps) => {
                        if (props.event.key === 'Escape') {
                            closePopup();
                            return true;
                        }
                        return false;
                    },

                    onExit: () => {
                        if (didIncrementOpenCountRef.current) {
                            decrementSuggestionMenuOpenCount(editor);
                            didIncrementOpenCountRef.current = false;
                        }
                        setInternalDecorationNode(null);
                        setInternalCommand(null);
                        setInternalItems([]);
                        setInternalQuery('');
                        setInternalRange(null);
                        // setInternalClientRect(null)
                        setShow(false);
                    },
                };
            },
            ...internalSuggestionPropsRef.current,
        });

        editor.registerPlugin(suggestion);

        return () => {
            if (!editor.isDestroyed) {
                editor.unregisterPlugin(pluginKey);
            }
        };
    }, [editor, pluginKey, closePopup]);

    const onSelect = useCallback(
        (item: SuggestionItem) => {
            closePopup();

            if (internalCommand) {
                internalCommand(item);
            }
        },
        [closePopup, internalCommand],
    );

    const { selectedIndex } = useMenuNavigation({
        editor: editor,
        query: internalQuery,
        items: internalItems,
        onSelect,
    });

    if (!isMounted || !show || !editor) {
        return null;
    }

    return (
        <div
            ref={ref}
            style={style}
            {...getFloatingProps()}
            data-selector={selector}
            className="tiptap-suggestion-menu"
            role="listbox"
            aria-label="Suggestions"
            onPointerDown={e => e.preventDefault()}
        >
            {children({
                items: internalItems,
                selectedIndex,
                onSelect,
            })}
        </div>
    );
};
